import { Stop } from '../types';
import { computeNights } from './nights';
import { loadJSON } from './dataLoader';

interface AccommodationData {
  id: number;
  name: string;
  type: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

// Cache for accommodations data
let accommodationsCache: AccommodationData[] | null = null;

async function loadAccommodations(): Promise<AccommodationData[]> {
  if (accommodationsCache) {
    return accommodationsCache;
  }
  
  try {
    accommodationsCache = await loadJSON<AccommodationData[]>(
      '/data/src/accommodations.json',
      { useGzip: false }
    );
    return accommodationsCache;
  } catch (error) {
    console.error('Failed to load accommodations:', error);
    return [];
  }
}

export interface AccommodationInfo {
  stop: Stop;
  name: string;
  address?: string;
  nights: number;
  coordinates?: { lat: number; lng: number };
}

/**
 * Get accommodation information for a specific day
 * Returns the accommodation stop for that day, if any
 * Includes address loaded from accommodations.json
 */
export async function getAccommodationForDay(
  stops: Stop[],
  day: string
): Promise<AccommodationInfo | null> {
  // Filter accommodation stops for this day
  const accommodationStops = stops.filter(
    stop => stop.type === 'accommodation' && stop.date === day
  );

  if (accommodationStops.length === 0) {
    return null;
  }

  // If multiple accommodations exist for the day, take the last one
  // (usually the one in the "night" slot)
  const accommodationStop = accommodationStops[accommodationStops.length - 1];

  // Calculate nights
  const nightsMap = computeNights(stops);
  const nights = nightsMap.get(accommodationStop.id) || 1;

  // Load accommodations data to get address
  const accommodations = await loadAccommodations();
  const accommodationData = accommodations.find(
    acc => acc.name === accommodationStop.name
  );

  return {
    stop: accommodationStop,
    name: accommodationStop.name,
    address: accommodationData?.address,
    nights,
    coordinates: {
      lat: accommodationStop.lat,
      lng: accommodationStop.lng,
    },
  };
}

/**
 * Synchronous version that doesn't load address (for initial render)
 * Can be used if address loading causes issues
 */
export function getAccommodationForDaySync(
  stops: Stop[],
  day: string
): AccommodationInfo | null {
  const accommodationStops = stops.filter(
    stop => stop.type === 'accommodation' && stop.date === day
  );

  if (accommodationStops.length === 0) {
    return null;
  }

  const accommodationStop = accommodationStops[accommodationStops.length - 1];
  const nightsMap = computeNights(stops);
  const nights = nightsMap.get(accommodationStop.id) || 1;

  return {
    stop: accommodationStop,
    name: accommodationStop.name,
    address: undefined, // Will be loaded separately if needed
    nights,
    coordinates: {
      lat: accommodationStop.lat,
      lng: accommodationStop.lng,
    },
  };
}

